import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import usePageSearchIndex from '../hooks/usePageSearchIndex';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Sector,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import './Dashboard.css';

const getLastFinancialYearRange = () => ({
  startDate: '2025-04-01',
  endDate: '2026-03-31'
});

const Dashboard = () => {
  const { isMarathi } = useLanguage();

  usePageSearchIndex({
    mr: ['डॅशबोर्ड', 'सदस्यत्व डेटा', 'प्रतिष्ठान', 'DBT योजना', 'आकडेवारी'],
    en: ['dashboard', 'membership data', 'establishments', 'DBT scheme', 'statistics']
  });

  const [activeTab, setActiveTab] = useState(0);
  const [membershipData, setMembershipData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePieIndex, setActivePieIndex] = useState(null);
  
  const [newEstablishmentsData, setNewEstablishmentsData] = useState([]);
  const [establishmentsLoading, setEstablishmentsLoading] = useState(false);
  const [establishmentsError, setEstablishmentsError] = useState(null);
  const [activePieIndexEstablishments, setActivePieIndexEstablishments] = useState(null);

  const [dbtSchemeData, setDbtSchemeData] = useState([]);
  const [dbtLoading, setDbtLoading] = useState(false);
  const [dbtError, setDbtError] = useState(null);
  const [activePieIndexDbt, setActivePieIndexDbt] = useState(null);
  const [dbtStartDate, setDbtStartDate] = useState(() => getLastFinancialYearRange().startDate);
  const [dbtEndDate, setDbtEndDate] = useState(() => getLastFinancialYearRange().endDate);

  // Cache TTL: 2 minutes for frontend cache
  const CACHE_TTL = 2 * 60 * 1000;
  const CACHE_KEY = 'membership_data_cache';
  const ESTABLISHMENTS_CACHE_KEY = 'new_establishments_data_cache';
  const DBT_CACHE_KEY = 'dbt_scheme_data_cache';

  const pageHeaderData = {
    title: {
      marathi: 'डॅशबोर्ड',
      english: 'Dashboard'
    },
    subtitle: {
      marathi: 'महाराष्ट्र कामगार कल्याण मंडळ डॅशबोर्ड',
      english: 'Maharashtra Labour Welfare Board Dashboard'
    }
  };

  // Tab configuration with Marathi/English labels
  const tabs = [
    {
      id: 1,
      name: {
        marathi: 'सदस्यत्व डेटा',
        english: 'Membership Data'
      },
      key: 'membership'
    },
    {
      id: 2,
      name: {
        marathi: 'नवीन प्रतिष्ठान डेटा',
        english: 'New Establishments Data'
      },
      key: 'establishments'
    },
    {
      id: 3,
      name: {
        marathi: 'DBT योजना',
        english: 'DBT Scheme'
      },
      key: 'dbt'
    }
  ];

  // Get cached data from localStorage
  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp < CACHE_TTL) {
        return data; // Cache is valid
      }
      
      // Cache expired, remove it
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading cache:', error);
      return null;
    }
  }, [CACHE_KEY, CACHE_TTL]);

  // Save data to cache
  const saveToCache = useCallback((data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving cache:', error);
    }
  }, [CACHE_KEY]);

  // Get cached new establishments data from localStorage
  const getCachedEstablishmentsData = useCallback(() => {
    try {
      const cached = localStorage.getItem(ESTABLISHMENTS_CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp < CACHE_TTL) {
        return data; // Cache is valid
      }
      
      // Cache expired, remove it
      localStorage.removeItem(ESTABLISHMENTS_CACHE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading establishments cache:', error);
      return null;
    }
  }, [ESTABLISHMENTS_CACHE_KEY, CACHE_TTL]);

  // Save new establishments data to cache
  const saveEstablishmentsToCache = useCallback((data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(ESTABLISHMENTS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving establishments cache:', error);
    }
  }, [ESTABLISHMENTS_CACHE_KEY]);

  // Get cached DBT scheme data from localStorage
  const getCachedDbtData = useCallback((startDate, endDate) => {
    try {
      const cached = localStorage.getItem(DBT_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp, cacheStartDate, cacheEndDate } = JSON.parse(cached);
      const now = Date.now();

      if (
        now - timestamp < CACHE_TTL &&
        cacheStartDate === startDate &&
        cacheEndDate === endDate
      ) {
        return data;
      }

      localStorage.removeItem(DBT_CACHE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading DBT cache:', error);
      return null;
    }
  }, [DBT_CACHE_KEY, CACHE_TTL]);

  const saveDbtToCache = useCallback((data, startDate, endDate) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        cacheStartDate: startDate,
        cacheEndDate: endDate
      };
      localStorage.setItem(DBT_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving DBT cache:', error);
    }
  }, [DBT_CACHE_KEY]);

  // Fetch Membership Data from API
  const fetchMembershipData = useCallback(async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
    }
    setError(null);

    try {
      // Use backend proxy to avoid CORS issues
      const API_BASE = process.env.NODE_ENV === 'production' 
        ? 'https://adminconsole.mlwb.in/api' 
        : 'http://localhost:5000/api';
      const apiUrl = `${API_BASE}/membership`;
      
      const response = await fetch(apiUrl, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      
      // Validate data structure
      if (!Array.isArray(result)) {
        throw new Error('Invalid data format received');
      }
      
      // Save to cache
      saveToCache(result);
      
      // Update state
      setMembershipData(result);
      setError(null);
    } catch (err) {
      console.error('Error fetching membership data:', err);
      setError(err.message);
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [saveToCache]);

  // Fetch New Establishments Data from API
  const fetchNewEstablishmentsData = useCallback(async (isBackground = false) => {
    if (!isBackground) {
      setEstablishmentsLoading(true);
    }
    setEstablishmentsError(null);

    try {
      // Use backend proxy to avoid CORS issues
      const API_BASE = process.env.NODE_ENV === 'production' 
        ? 'https://adminconsole.mlwb.in/api' 
        : 'http://localhost:5000/api';
      const apiUrl = `${API_BASE}/new-establishments`;
      
      const response = await fetch(apiUrl, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      
      // Validate data structure
      if (!Array.isArray(result)) {
        throw new Error('Invalid data format received');
      }
      
      // Save to cache
      saveEstablishmentsToCache(result);
      
      // Update state
      setNewEstablishmentsData(result);
      setEstablishmentsError(null);
    } catch (err) {
      console.error('Error fetching new establishments data:', err);
      setEstablishmentsError(err.message);
    } finally {
      if (!isBackground) {
        setEstablishmentsLoading(false);
      }
    }
  }, [saveEstablishmentsToCache]);

  const fetchDbtSchemeData = useCallback(async ({ isBackground = false, startDate, endDate } = {}) => {
    const effectiveStart = startDate || dbtStartDate;
    const effectiveEnd = endDate || dbtEndDate;

    if (!effectiveStart || !effectiveEnd) {
      setDbtError(isMarathi() ? 'कृपया तारीख श्रेणी निवडा' : 'Please select a date range');
      return;
    }

    if (!isBackground) {
      setDbtLoading(true);
    }
    setDbtError(null);

    try {
      const API_BASE = process.env.NODE_ENV === 'production' 
        ? 'https://adminconsole.mlwb.in/api' 
        : 'http://localhost:5000/api';
      const apiUrl = `${API_BASE}/dbt-scheme`;

      // Build URL with query parameters
      const url = new URL(apiUrl);
      url.searchParams.set('startDate', effectiveStart);
      url.searchParams.set('endDate', effectiveEnd);

      const response = await fetch(url.toString(), {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const payload = await response.json();
      const data =
        Array.isArray(payload) ? payload :
        Array.isArray(payload?.data) ? payload.data : [];

      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }

      saveDbtToCache(data, effectiveStart, effectiveEnd);
      setDbtSchemeData(data);
      setDbtError(null);
    } catch (err) {
      console.error('Error fetching DBT scheme data:', err);
      setDbtError(err.message);
    } finally {
      if (!isBackground) {
        setDbtLoading(false);
      }
    }
  }, [dbtStartDate, dbtEndDate, isMarathi, saveDbtToCache]);

  // Load data when Membership Data tab is active (lazy loading)
  useEffect(() => {
    if (activeTab === 0) {
      // Check cache first
      const cachedData = getCachedData();
      if (cachedData) {
        setMembershipData(cachedData);
        setLoading(false);
        // Fetch fresh data in background
        fetchMembershipData(true);
      } else {
        // No cache, fetch fresh data
        fetchMembershipData(false);
      }
    }
  }, [activeTab, getCachedData, fetchMembershipData]);

  // Load data when New Establishments Data tab is active (lazy loading)
  useEffect(() => {
    if (activeTab === 1) {
      // Check cache first
      const cachedData = getCachedEstablishmentsData();
      if (cachedData) {
        setNewEstablishmentsData(cachedData);
        setEstablishmentsLoading(false);
        // Fetch fresh data in background
        fetchNewEstablishmentsData(true);
      } else {
        // No cache, fetch fresh data
        fetchNewEstablishmentsData(false);
      }
    }
  }, [activeTab, getCachedEstablishmentsData, fetchNewEstablishmentsData]);

  // Load data when DBT Scheme tab is active (lazy loading)
  useEffect(() => {
    if (activeTab === 2) {
      const cachedData = getCachedDbtData(dbtStartDate, dbtEndDate);
      if (cachedData) {
        setDbtSchemeData(cachedData);
        setDbtLoading(false);
        fetchDbtSchemeData({ isBackground: true, startDate: dbtStartDate, endDate: dbtEndDate });
      } else {
        fetchDbtSchemeData({ isBackground: false, startDate: dbtStartDate, endDate: dbtEndDate });
      }
    }
  }, [activeTab, dbtStartDate, dbtEndDate, getCachedDbtData, fetchDbtSchemeData]);

  // Reset DBT date filters when leaving the tab
  useEffect(() => {
    if (activeTab !== 2) {
      const range = getLastFinancialYearRange();
      setDbtStartDate(range.startDate);
      setDbtEndDate(range.endDate);
    }
  }, [activeTab]);

  // Helper function to parse branch name (handles "English/Marathi" or "English /Marathi" format)
  const parseBranchName = (branchName) => {
    if (!branchName) return { english: 'Unknown', marathi: 'Unknown' };
    
    // Check if branch name contains "/" separator (both languages)
    // Handles formats like "Mumbai/मुंबई" or "Pune /पुणे" (with spaces)
    if (branchName.includes('/')) {
      const parts = branchName.split('/').map(part => part.trim());
      const english = parts[0] || branchName;
      const marathi = parts[1] || parts[0] || branchName;
      
      return {
        english: english.trim(),
        marathi: marathi.trim()
      };
    }
    
    // If no separator, assume it's English only
    return {
      english: branchName.trim(),
      marathi: branchName.trim()
    };
  };

  // Helper function to parse division name (handles "English/Marathi" format)
  const parseDivisionName = (divisionName) => {
    if (!divisionName) return { english: 'Unknown', marathi: 'Unknown' };
    
    // Check if division name contains "/" separator (both languages)
    if (divisionName.includes('/')) {
      const parts = divisionName.split('/').map(part => part.trim());
      const english = parts[0] || divisionName;
      const marathi = parts[1] || parts[0] || divisionName;
      
      return {
        english: english.trim(),
        marathi: marathi.trim()
      };
    }
    
    // If no separator, assume it's English only
    return {
      english: divisionName.trim(),
      marathi: divisionName.trim()
    };
  };

  const parseSchemeName = (schemeName) => {
    if (!schemeName) return { english: 'Unknown', marathi: 'Unknown' };

    const containsLatin = (text) => /[A-Za-z]/.test(text);
    const containsDevanagari = (text) => /[\u0900-\u097F]/.test(text);

    if (schemeName.includes('/')) {
      const parts = schemeName.split('/').map(part => part.trim()).filter(Boolean);
      let english = parts.find(containsLatin);
      let marathi = parts.find(containsDevanagari);

      // Fallbacks if detection fails
      if (!english) {
        english = parts[1] || parts[0];
      }
      if (!marathi) {
        marathi = parts[0] || parts[1];
      }

      return {
        english: (english || schemeName).trim(),
        marathi: (marathi || schemeName).trim()
      };
    }

    return {
      english: schemeName.trim(),
      marathi: schemeName.trim()
    };
  };

  const handleDbtApplyFilter = useCallback(() => {
    if (!dbtStartDate || !dbtEndDate) {
      setDbtError(isMarathi() ? 'कृपया तारीख श्रेणी निवडा' : 'Please select a date range');
      return;
    }
    fetchDbtSchemeData({ startDate: dbtStartDate, endDate: dbtEndDate });
  }, [dbtStartDate, dbtEndDate, fetchDbtSchemeData, isMarathi]);

  const handleDbtResetFilter = useCallback(() => {
    const range = getLastFinancialYearRange();
    setDbtStartDate(range.startDate);
    setDbtEndDate(range.endDate);
    fetchDbtSchemeData({ startDate: range.startDate, endDate: range.endDate });
  }, [fetchDbtSchemeData]);

  // Memoized processed data for charts (branch-wise aggregation)
  const chartData = useMemo(() => {
    if (!Array.isArray(membershipData) || membershipData.length === 0) {
      return [];
    }

    // Process data for charts (branch-wise aggregation)
    const branchWiseData = membershipData.reduce((acc, item) => {
      const branchFull = item.branch_name || 'Unknown';
      const parsed = parseBranchName(branchFull);
      
      // Use full branch name as key to group data correctly
      if (!acc[branchFull]) {
        acc[branchFull] = { 
          branch: branchFull, // Keep original for grouping
          branchEnglish: parsed.english,
          branchMarathi: parsed.marathi,
          branchDisplay: isMarathi() ? parsed.marathi : parsed.english, // Language-aware display
          mem: 0, 
          fam: 0, 
          oth: 0, 
          total: 0 
        };
      }
      acc[branchFull].mem += item.mem || 0;
      acc[branchFull].fam += item.fam || 0;
      acc[branchFull].oth += item.oth || 0;
      acc[branchFull].total += item.total || 0;
      return acc;
    }, {});

    // Update branchDisplay for all entries based on current language
    const result = Object.values(branchWiseData);
    result.forEach(item => {
      item.branchDisplay = isMarathi() ? item.branchMarathi : item.branchEnglish;
    });

    return result;
  }, [membershipData, isMarathi]); // Add isMarathi as dependency

  // Memoized processed data for establishments charts (division-wise aggregation)
  const establishmentsChartData = useMemo(() => {
    if (!Array.isArray(newEstablishmentsData) || newEstablishmentsData.length === 0) {
      return [];
    }

    // Process data for charts (division-wise aggregation)
    const divisionWiseData = newEstablishmentsData.reduce((acc, item) => {
      const divisionFull = item.division_name || 'Unknown';
      const parsed = parseDivisionName(divisionFull);
      
      if (!acc[divisionFull]) {
        acc[divisionFull] = {
          division: divisionFull,
          divisionEnglish: parsed.english,
          divisionMarathi: parsed.marathi,
          divisionDisplay: isMarathi() ? parsed.marathi : parsed.english,
          new_esta: 0,
          closed_esta: 0,
          total: 0
        };
      }
      acc[divisionFull].new_esta += item.new_esta || 0;
      acc[divisionFull].closed_esta += item.closed_esta || 0;
      acc[divisionFull].total += (item.new_esta || 0) + (item.closed_esta || 0);
      return acc;
    }, {});

    // Update divisionDisplay for all entries based on current language
    const result = Object.values(divisionWiseData);
    result.forEach(item => {
      item.divisionDisplay = isMarathi() ? item.divisionMarathi : item.divisionEnglish;
    });

    return result;
  }, [newEstablishmentsData, isMarathi]);

  const dbtDivisionChartData = useMemo(() => {
    if (!Array.isArray(dbtSchemeData) || dbtSchemeData.length === 0) {
      return [];
    }

    const divisionWiseData = dbtSchemeData.reduce((acc, item) => {
      const divisionFull = item.div_name || 'Unknown';
      const parsed = parseDivisionName(divisionFull);

      if (!acc[divisionFull]) {
        acc[divisionFull] = {
          division: divisionFull,
          divisionEnglish: parsed.english,
          divisionMarathi: parsed.marathi,
          divisionDisplay: isMarathi() ? parsed.marathi : parsed.english,
          newCount: 0,
          acfCount: 0,
          aprCount: 0,
          rejCount: 0,
          rtnCount: 0,
          total: 0
        };
      }

      acc[divisionFull].newCount += item.new || 0;
      acc[divisionFull].acfCount += item.acf || 0;
      acc[divisionFull].aprCount += item.apr || 0;
      acc[divisionFull].rejCount += item.rej || 0;
      acc[divisionFull].rtnCount += item.rtn || 0;
      acc[divisionFull].total += (item.new || 0) + (item.acf || 0) + (item.apr || 0) + (item.rej || 0) + (item.rtn || 0);

      return acc;
    }, {});

    const result = Object.values(divisionWiseData);
    result.forEach(item => {
      item.divisionDisplay = isMarathi() ? item.divisionMarathi : item.divisionEnglish;
    });

    return result;
  }, [dbtSchemeData, isMarathi]);

  const dbtSchemeChartData = useMemo(() => {
    if (!Array.isArray(dbtSchemeData) || dbtSchemeData.length === 0) {
      return [];
    }

    const schemeWiseData = dbtSchemeData.reduce((acc, item) => {
      const schemeFull = item.sch_name || 'Unknown';
      const parsed = parseSchemeName(schemeFull);

      if (!acc[schemeFull]) {
        acc[schemeFull] = {
          scheme: schemeFull,
          schemeEnglish: parsed.english,
          schemeMarathi: parsed.marathi,
          schemeDisplay: isMarathi() ? parsed.marathi : parsed.english,
          total: 0
        };
      }

      const totalEntry = (item.new || 0) + (item.acf || 0) + (item.apr || 0) + (item.rej || 0) + (item.rtn || 0);
      acc[schemeFull].total += totalEntry;
      return acc;
    }, {});

    const result = Object.values(schemeWiseData);
    result.forEach(item => {
      item.schemeDisplay = isMarathi() ? item.schemeMarathi : item.schemeEnglish;
    });
    return result;
  }, [dbtSchemeData, isMarathi]);

  // Chart colors
  const COLORS = ['#3b82f6', '#22c55e', '#facc15', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

  // Custom Tooltip component for charts (language-aware)
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      // Get the data entry to access branchDisplay or divisionDisplay
      const dataEntry = payload[0]?.payload || {};
      // Check for both branchDisplay (membership) and divisionDisplay (establishments)
      const displayName = dataEntry.branchDisplay || dataEntry.divisionDisplay || dataEntry.schemeDisplay || dataEntry.branch || dataEntry.division || dataEntry.scheme || 'Unknown';
      const total = payload[0]?.value || 0;
      
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ color: payload[0]?.color || '#333', margin: '4px 0', fontWeight: '500' }}>
            {`${displayName}: ${total.toLocaleString()}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Active shape for pie chart (explode effect on hover)
  const renderActiveShape = (props) => {
    const {
      cx, cy, innerRadius, outerRadius, startAngle, endAngle,
      fill
    } = props;

    // Increase outer radius when hovered (explode effect)
    const activeOuterRadius = outerRadius + 15;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={activeOuterRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  // Render New Establishments Data tab content
  const renderNewEstablishmentsData = () => {
    if (establishmentsLoading && newEstablishmentsData.length === 0) {
      return (
        <div className="tab-content-wrapper">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">
              {isMarathi() ? 'डेटा लोड होत आहे...' : 'Loading data...'}
            </p>
          </div>
        </div>
      );
    }

    if (establishmentsError) {
      return (
        <div className="tab-content-wrapper">
          <div className="error-container">
            <p className="error-message">
              {isMarathi() ? 'त्रुटी: ' : 'Error: '}{establishmentsError}
            </p>
            <button
              className="retry-button"
              onClick={() => fetchNewEstablishmentsData(false)}
              type="button"
            >
              {isMarathi() ? 'पुन्हा प्रयत्न करा' : 'Retry'}
            </button>
          </div>
        </div>
      );
    }

    if (newEstablishmentsData.length === 0) {
      return (
        <div className="tab-content-wrapper">
          <div className="tab-placeholder">
            <h3>{isMarathi() ? 'नवीन प्रतिष्ठान डेटा' : 'New Establishments Data'}</h3>
            <p>{isMarathi() 
              ? 'डेटा उपलब्ध नाही'
              : 'No data available'}
            </p>
          </div>
        </div>
      );
    }

    // Calculate summary statistics
    const totalNewEstab = newEstablishmentsData.reduce((sum, item) => sum + (item.new_esta || 0), 0);
    const totalClosedEstab = newEstablishmentsData.reduce((sum, item) => sum + (item.closed_esta || 0), 0);
    const uniqueDivisions = new Set(newEstablishmentsData.map(item => item.division_id)).size;
    const uniqueDistricts = new Set(newEstablishmentsData.map(item => item.district_name)).size;

    return (
      <div className="tab-content-wrapper dbt-tab-wrapper">
        {/* Summary Stats */}
        <div className="stats-summary">
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण रेकॉर्ड' : 'Total Records'}</h4>
            <p className="stat-value">{newEstablishmentsData.length}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण विभाग' : 'Total Divisions'}</h4>
            <p className="stat-value">{uniqueDivisions}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण जिल्हे' : 'Total Districts'}</h4>
            <p className="stat-value">{uniqueDistricts}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'नवीन प्रतिष्ठाने' : 'New Establishments'}</h4>
            <p className="stat-value">{totalNewEstab.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'बंद प्रतिष्ठाने' : 'Closed Establishments'}</h4>
            <p className="stat-value">{totalClosedEstab.toLocaleString()}</p>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          {/* Bar Chart */}
          <div className="chart-card">
            <h3 className="chart-title">
              {isMarathi() ? 'विभागवार आकडेवारी' : 'Division-wise Statistics'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={establishmentsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="divisionDisplay"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="new_esta" fill="#22c55e" name={isMarathi() ? 'नवीन प्रतिष्ठाने' : 'New Establishments'} />
                <Bar dataKey="closed_esta" fill="#ef4444" name={isMarathi() ? 'बंद प्रतिष्ठाने' : 'Closed Establishments'} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart */}
          <div className="chart-card">
            <h3 className="chart-title">
              {isMarathi() ? 'एकूण वितरण' : 'Total Distribution'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  activeIndex={activePieIndexEstablishments}
                  activeShape={renderActiveShape}
                  data={establishmentsChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry, index) => {
                    if (activePieIndexEstablishments !== null && index !== activePieIndexEstablishments) {
                      return '';
                    }
                    const divisionName = entry.divisionDisplay || entry.division || 'Unknown';
                    const total = entry.total || 0;
                    return `${divisionName}: ${total.toLocaleString()}`;
                  }}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="total"
                  onMouseEnter={(data, index) => {
                    setActivePieIndexEstablishments(index);
                  }}
                  onMouseLeave={() => {
                    setActivePieIndexEstablishments(null);
                  }}
                >
                  {establishmentsChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '8px',
              marginTop: '10px',
              flexWrap: 'wrap',
              paddingTop: '10px'
            }}>
              {/* Show each division with its color and total */}
              {establishmentsChartData.map((entry, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  {/* Color indicator */}
                  <div 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: COLORS[index % COLORS.length],
                      borderRadius: '2px',
                      flexShrink: 0
                    }} 
                  />
                  {/* Division name and total */}
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#333',
                    fontWeight: '500'
                  }}>
                    {entry.divisionDisplay}: <strong style={{ color: COLORS[index % COLORS.length] }}>{entry.total?.toLocaleString() || 0}</strong>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Line Chart */}
        <div className="chart-card full-width">
          <h3 className="chart-title">
            {isMarathi() ? 'विभागवार एकूण तुलना' : 'Division-wise Total Comparison'}
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={establishmentsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="divisionDisplay"
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#8b5cf6"
                strokeWidth={3}
                name={isMarathi() ? 'एकूण' : 'Total'}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Data Table */}
        <div className="data-table-container">
          <h3 className="table-title">
            {isMarathi() ? 'तपशीलवार डेटा' : 'Detailed Data'}
          </h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{isMarathi() ? 'विभाग' : 'Division'}</th>
                  <th>{isMarathi() ? 'जिल्हा' : 'District'}</th>
                  <th>{isMarathi() ? 'नवीन प्रतिष्ठाने' : 'New Establishments'}</th>
                  <th>{isMarathi() ? 'बंद प्रतिष्ठाने' : 'Closed Establishments'}</th>
                </tr>
              </thead>
              <tbody>
                {newEstablishmentsData.map((item, index) => {
                  const parsedDivision = parseDivisionName(item.division_name || '');
                  return (
                    <tr key={index}>
                      <td>{isMarathi() ? parsedDivision.marathi : parsedDivision.english}</td>
                      <td>
                        {isMarathi()
                          ? item.district_name_ol || item.district_name || '-'
                          : item.district_name || '-'}
                      </td>
                      <td>{item.new_esta?.toLocaleString() || 0}</td>
                      <td>{item.closed_esta?.toLocaleString() || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderDbtSchemeData = () => {
    if (dbtLoading && dbtSchemeData.length === 0) {
      return (
        <div className="tab-content-wrapper">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">
              {isMarathi() ? 'डेटा लोड होत आहे...' : 'Loading data...'}
            </p>
          </div>
        </div>
      );
    }

    if (dbtError) {
      return (
        <div className="tab-content-wrapper">
          <div className="error-container">
            <p className="error-message">
              {isMarathi() ? 'त्रुटी: ' : 'Error: '}{dbtError}
            </p>
              <button
                className="retry-button"
                onClick={() =>
                  fetchDbtSchemeData({
                    isBackground: false,
                    startDate: dbtStartDate,
                    endDate: dbtEndDate,
                  })
                }
                type="button"
              >
              {isMarathi() ? 'पुन्हा प्रयत्न करा' : 'Retry'}
            </button>
          </div>
        </div>
      );
    }

    if (dbtSchemeData.length === 0) {
      return (
        <div className="tab-content-wrapper">
          <div className="tab-placeholder">
            <h3>{isMarathi() ? 'DBT योजना' : 'DBT Scheme'}</h3>
            <p>{isMarathi() ? 'डेटा उपलब्ध नाही' : 'No data available'}</p>
          </div>
        </div>
      );
    }

    const totalNew = dbtSchemeData.reduce((sum, item) => sum + (item.new || 0), 0);
    const totalAcf = dbtSchemeData.reduce((sum, item) => sum + (item.acf || 0), 0);
    const totalApr = dbtSchemeData.reduce((sum, item) => sum + (item.apr || 0), 0);
    const totalRej = dbtSchemeData.reduce((sum, item) => sum + (item.rej || 0), 0);
    const totalRtn = dbtSchemeData.reduce((sum, item) => sum + (item.rtn || 0), 0);
    const totalApplications = totalNew + totalAcf + totalApr + totalRej + totalRtn;
    const uniqueDivisions = new Set(dbtSchemeData.map(item => item.div_name)).size;
    const uniqueSchemes = new Set(dbtSchemeData.map(item => item.sch_name)).size;
    const uniqueDistricts = new Set(dbtSchemeData.map(item => item.district_name || item.district_name_ol).filter(Boolean)).size;

    return (
      <div className="tab-content-wrapper">
        <div className="filters-row fancy-filter">
          <div className="filter-panel">
            <div className="filter-field">
              <label>
              <span className="filter-subtitle">{isMarathi() ? 'सुरुवातीची तारीख' : 'Start Date'}</span>
              </label>
              <div className="filter-input-wrapper">
                <span className="filter-icon">📅</span>
                <input
                  type="date"
                  value={dbtStartDate}
                  onChange={(e) => setDbtStartDate(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-divider" />
            <div className="filter-field">
              <label>
              <span className="filter-subtitle">{isMarathi() ? 'शेवटची तारीख' : 'End Date'}</span>
              </label>
              <div className="filter-input-wrapper">
                <span className="filter-icon">📅</span>
                <input
                  type="date"
                  value={dbtEndDate}
                  onChange={(e) => setDbtEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-actions">
              <button
                className="primary-action"
                type="button"
                onClick={handleDbtApplyFilter}
                disabled={dbtLoading || !dbtStartDate || !dbtEndDate}
              >
                {dbtLoading ? (isMarathi() ? 'लोड होत आहे...' : 'Loading...') : (isMarathi() ? 'फिल्टर लागू करा' : 'Apply Filter')}
              </button>
              <button
                className="secondary-action"
                type="button"
                onClick={handleDbtResetFilter}
                disabled={dbtLoading}
              >
                {isMarathi() ? 'रीसेट' : 'Reset'}
              </button>
            </div>
          </div>
        </div>

        <div className="stats-summary">
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण अर्ज' : 'Total Applications'}</h4>
            <p className="stat-value">{totalApplications.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'नवीन' : 'New'}</h4>
            <p className="stat-value">{totalNew.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'ACF' : 'ACF'}</h4>
            <p className="stat-value">{totalAcf.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'मंजूर' : 'Approved'}</h4>
            <p className="stat-value">{totalApr.toLocaleString()}</p>
          </div>
          <div className="stat-card danger-card">
            <h4>{isMarathi() ? 'नकार' : 'Rejected'}</h4>
            <p className="stat-value">{totalRej.toLocaleString()}</p>
          </div>
          <div className="stat-card danger-card light">
            <h4>{isMarathi() ? 'परत' : 'Returned'}</h4>
            <p className="stat-value">{totalRtn.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'विभाग' : 'Divisions'}</h4>
            <p className="stat-value">{uniqueDivisions}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'योजना' : 'Schemes'}</h4>
            <p className="stat-value">{uniqueSchemes}</p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'जिल्हे' : 'Districts'}</h4>
            <p className="stat-value">{uniqueDistricts}</p>
          </div>
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <h3 className="chart-title">
              {isMarathi() ? 'विभागवार स्थिती' : 'Division-wise Status'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dbtDivisionChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="divisionDisplay"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="newCount" fill="#3b82f6" name={isMarathi() ? 'नवीन' : 'New'} />
                <Bar dataKey="acfCount" fill="#22c55e" name="ACF" />
                <Bar dataKey="aprCount" fill="#06b6d4" name={isMarathi() ? 'मंजूर' : 'Approved'} />
                <Bar dataKey="rejCount" fill="#ef4444" name={isMarathi() ? 'नकार' : 'Rejected'} />
                <Bar dataKey="rtnCount" fill="#f97316" name={isMarathi() ? 'परत' : 'Returned'} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3 className="chart-title">
              {isMarathi() ? 'योजनानुसार वितरण' : 'Scheme-wise Distribution'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  activeIndex={activePieIndexDbt}
                  activeShape={renderActiveShape}
                  data={dbtSchemeChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry, index) => {
                    if (activePieIndexDbt !== null && index !== activePieIndexDbt) {
                      return '';
                    }
                    const schemeName = entry.schemeDisplay || entry.scheme || 'Unknown';
                    const total = entry.total || 0;
                    return `${schemeName}: ${total.toLocaleString()}`;
                  }}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="total"
                  onMouseEnter={(data, index) => {
                    setActivePieIndexDbt(index);
                  }}
                  onMouseLeave={() => {
                    setActivePieIndexDbt(null);
                  }}
                >
                  {dbtSchemeChartData.map((entry, index) => (
                    <Cell key={`dbt-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '8px',
              marginTop: '10px',
              flexWrap: 'wrap',
              paddingTop: '10px'
            }}>
              {/* Show each scheme with its color and total */}
              {dbtSchemeChartData.map((entry, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  {/* Color indicator */}
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: COLORS[index % COLORS.length],
                      borderRadius: '2px',
                      flexShrink: 0
                    }}
                  />
                  {/* Scheme name and total */}
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#333',
                    fontWeight: '500'
                  }}>
                    {entry.schemeDisplay}: <strong style={{ color: COLORS[index % COLORS.length] }}>{entry.total?.toLocaleString() || 0}</strong>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="chart-card full-width">
          <h3 className="chart-title">
            {isMarathi() ? 'विभागवार एकूण अर्ज' : 'Division-wise Total Applications'}
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={dbtDivisionChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="divisionDisplay"
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#8b5cf6"
                strokeWidth={3}
                name={isMarathi() ? 'एकूण' : 'Total'}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="data-table-container">
          <h3 className="table-title">
            {isMarathi() ? 'तपशीलवार DBT डेटा' : 'Detailed DBT Data'}
          </h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{isMarathi() ? 'विभाग' : 'Division'}</th>
                  <th>{isMarathi() ? 'योजना' : 'Scheme'}</th>
                  <th>{isMarathi() ? 'जिल्हा' : 'District'}</th>
                  <th>{isMarathi() ? 'नवीन' : 'New'}</th>
                  <th>ACF</th>
                  <th>{isMarathi() ? 'मंजूर' : 'Approved'}</th>
                  <th>{isMarathi() ? 'नकार' : 'Rejected'}</th>
                  <th>{isMarathi() ? 'परत' : 'Returned'}</th>
                </tr>
              </thead>
              <tbody>
                {dbtSchemeData.map((item, index) => {
                  const parsedDivision = parseDivisionName(item.div_name || '');
                  const parsedScheme = parseSchemeName(item.sch_name || '');

                  return (
                    <tr key={index}>
                      <td>{isMarathi() ? parsedDivision.marathi : parsedDivision.english}</td>
                      <td>{isMarathi() ? parsedScheme.marathi : parsedScheme.english}</td>
                      <td>
                        {isMarathi()
                          ? item.district_name_ol || item.district_name || '-'
                          : item.district_name || '-'}
                      </td>
                      <td>{item.new?.toLocaleString() || 0}</td>
                      <td>{item.acf?.toLocaleString() || 0}</td>
                      <td>{item.apr?.toLocaleString() || 0}</td>
                      <td>{item.rej?.toLocaleString() || 0}</td>
                      <td>{item.rtn?.toLocaleString() || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Render Membership Data tab content
  const renderMembershipData = () => {
    if (loading && membershipData.length === 0) {
      return (
        <div className="tab-content-wrapper">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">
              {isMarathi() ? 'डेटा लोड होत आहे...' : 'Loading data...'}
            </p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="tab-content-wrapper">
          <div className="error-container">
            <p className="error-message">
              {isMarathi() ? 'त्रुटी: ' : 'Error: '}{error}
            </p>
            <button
              className="retry-button"
              onClick={() => fetchMembershipData(false)}
              type="button"
            >
              {isMarathi() ? 'पुन्हा प्रयत्न करा' : 'Retry'}
            </button>
          </div>
        </div>
      );
    }

    if (membershipData.length === 0) {
      return (
        <div className="tab-content-wrapper">
          <div className="tab-placeholder">
            <h3>{isMarathi() ? 'सदस्यत्व डेटा' : 'Membership Data'}</h3>
            <p>{isMarathi() 
              ? 'डेटा उपलब्ध नाही'
              : 'No data available'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="tab-content-wrapper">
        {/* Summary Stats */}
        <div className="stats-summary">
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण सदस्य' : 'Total Members'}</h4>
            <p className="stat-value">
              {membershipData.reduce((sum, item) => sum + (item.mem || 0), 0).toLocaleString()}
            </p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण कुटुंबे' : 'Total Families'}</h4>
            <p className="stat-value">
              {membershipData.reduce((sum, item) => sum + (item.fam || 0), 0).toLocaleString()}
            </p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'इतर' : 'Total Others'}</h4>
            <p className="stat-value">
              {membershipData.reduce((sum, item) => sum + (item.oth || 0), 0).toLocaleString()}
            </p>
          </div>
          <div className="stat-card">
            <h4>{isMarathi() ? 'एकूण' : 'Grand Total'}</h4>
            <p className="stat-value">
              {membershipData.reduce((sum, item) => sum + (item.total || 0), 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Charts Grid */}
            <div className="charts-grid">
          {/* Bar Chart */}
          <div className="chart-card">
            <h3 className="chart-title">
              {isMarathi() ? 'शाखावार आकडेवारी' : 'Branch-wise Statistics'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="branchDisplay"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="mem" fill="#3b82f6" name={isMarathi() ? 'सदस्य' : 'Members'} />
                <Bar dataKey="fam" fill="#22c55e" name={isMarathi() ? 'कुटुंबे' : 'Families'} />
                <Bar dataKey="oth" fill="#facc15" name={isMarathi() ? 'इतर' : 'Others'} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart */}
          <div className="chart-card">
            <h3 className="chart-title">
              {isMarathi() ? 'एकूण वितरण' : 'Total Distribution'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  activeIndex={activePieIndex}
                  activeShape={renderActiveShape}
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry, index) => {
                    // Only show label for the active/hovered segment
                    if (activePieIndex !== null && index !== activePieIndex) {
                      return ''; // Hide other labels when hovering
                    }
                    // Show full branch name (no truncation)
                    const branchName = entry.branchDisplay || entry.branch || 'Unknown';
                    const total = entry.total || 0;
                    return `${branchName}: ${total.toLocaleString()}`;
                  }}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="total"
                  onMouseEnter={(data, index) => {
                    setActivePieIndex(index);
                  }}
                  onMouseLeave={() => {
                    setActivePieIndex(null);
                  }}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Custom Legend - Show text first, then all colors */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '8px',
              marginTop: '10px',
              flexWrap: 'wrap',
              paddingTop: '10px'
            }}>
              {/* Text label first */}
              {/* Show each branch with its color and total */}
              {chartData.map((entry, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  {/* Color indicator */}
                  <div 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: COLORS[index % COLORS.length],
                      borderRadius: '2px',
                      flexShrink: 0
                    }} 
                  />
                  {/* Branch name and total */}
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#333',
                    fontWeight: '500'
                  }}>
                    {entry.branchDisplay}: <strong style={{ color: COLORS[index % COLORS.length] }}>{entry.total?.toLocaleString() || 0}</strong>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Line Chart */}
        <div className="chart-card full-width">
          <h3 className="chart-title">
            {isMarathi() ? 'शाखावार एकूण तुलना' : 'Branch-wise Total Comparison'}
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="branchDisplay"
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#8b5cf6"
                strokeWidth={3}
                name={isMarathi() ? 'एकूण' : 'Total'}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Data Table */}
        <div className="data-table-container">
          <h3 className="table-title">
            {isMarathi() ? 'तपशीलवार डेटा' : 'Detailed Data'}
          </h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{isMarathi() ? 'शाखा' : 'Branch'}</th>
                  <th>{isMarathi() ? 'जिल्हा' : 'District'}</th>
                  <th>{isMarathi() ? 'सदस्य' : 'Members'}</th>
                  <th>{isMarathi() ? 'कुटुंबे' : 'Families'}</th>
                  <th>{isMarathi() ? 'इतर' : 'Others'}</th>
                  <th>{isMarathi() ? 'एकूण' : 'Total'}</th>
                </tr>
              </thead>
              <tbody>
                {membershipData.map((item, index) => {
                  const parsedBranch = parseBranchName(item.branch_name || '');
                  return (
                    <tr key={index}>
                      <td>{isMarathi() ? parsedBranch.marathi : parsedBranch.english}</td>
                      <td>
                        {isMarathi()
                          ? item.district_name_ol || item.district_name || '-'
                          : item.district_name || '-'}
                      </td>
                      <td>{item.mem?.toLocaleString() || 0}</td>
                      <td>{item.fam?.toLocaleString() || 0}</td>
                      <td>{item.oth?.toLocaleString() || 0}</td>
                      <td className="total-cell">{item.total?.toLocaleString() || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderMembershipData();
      case 1:
        return renderNewEstablishmentsData();
      case 2:
        return renderDbtSchemeData();
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <PageHeader 
        title={pageHeaderData.title}
        subtitle={pageHeaderData.subtitle}
      />
      <div className="dashboard-content">
        <div className="dashboard-tabs-container">
          <div className="dashboard-tabs-header">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                className={`dashboard-tab-button ${activeTab === index ? 'active' : ''}`}
                onClick={() => setActiveTab(index)}
                type="button"
              >
                {isMarathi() ? tab.name.marathi : tab.name.english}
              </button>
            ))}
          </div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
